import type { ILogger } from '../logging/Logger';
import type { ILegacyContentService, ServiceResult, PaginationParams, PaginatedResult } from '../interfaces/IDataService';
import { publicAPI, type LegacyContent as APILegacyContent } from '../../../lib/api';
import { ValidationError } from '../../../utils/errorHandler';
import type { LegacyContent } from '../../../types/index';

/**
 * LegacyContentService Implementation
 * 
 * Provides legacy content management functionality with proper API integration.
 * Uses the publicAPI.getLegacyContent() method for data retrieval.
 * 
 * Features:
 * - Basic content listing with pagination
 * - Category-based filtering
 * - Published content filtering
 * - Simple search functionality
 * - Comprehensive error handling and logging
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */
export class LegacyContentService implements ILegacyContentService {
  private readonly logger: ILogger;

  constructor(logger: ILogger) {
    this.logger = logger;
  }

  /**
   * Convert API LegacyContent to interface LegacyContent
   */
  private mapApiContent(apiContent: APILegacyContent): LegacyContent {
    return {
      ...apiContent,
      // Ensure compatibility if needed
    } as LegacyContent;
  }

  /**
   * Get all legacy content with pagination
   */
  async getAll(params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<LegacyContent>>> {
    try {
      this.logger.debug('LegacyContentService.getAll called', 'LegacyContentService', { params });

      const { page = 1, limit = 10, sortBy = 'date_created', sortOrder = 'desc' } = params;

      // Validate pagination parameters
      if (page < 1 || limit < 1 || limit > 100) {
        return {
          success: false,
          error: 'Invalid pagination parameters. Page must be >= 1, limit must be between 1 and 100.'
        };
      }

      const apiContent = await publicAPI.getLegacyContent();
      let content = apiContent.map(item => this.mapApiContent(item));

      // Apply sorting
      content = this.applySorting(content, sortBy, sortOrder);

      // Apply pagination
      const total = content.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedContent = content.slice(startIndex, endIndex);

      const result: PaginatedResult<LegacyContent> = {
        items: paginatedContent,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Legacy content retrieved successfully', 'LegacyContentService', {
        total,
        page,
        itemCount: paginatedContent.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in LegacyContentService.getAll', 'LegacyContentService', { 
        error: (error as Error).message,
        params 
      });
      
      if (error instanceof ValidationError) {
        return { success: false, error: error.message };
      }
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching legacy content' 
      };
    }
  }

  /**
   * Get legacy content by ID
   */
  async getById(id: string): Promise<ServiceResult<LegacyContent>> {
    try {
      this.logger.debug('LegacyContentService.getById called', 'LegacyContentService', { id });

      if (!id || typeof id !== 'string') {
        return { success: false, error: 'Invalid content ID provided' };
      }

      const apiContent = await publicAPI.getLegacyContent();
      const apiItem = apiContent.find(item => item.id === id);

      if (!apiItem) {
        this.logger.warn('Legacy content not found', 'LegacyContentService', { id });
        return { success: false, error: 'Legacy content not found' };
      }

      const content = this.mapApiContent(apiItem);
      this.logger.debug('Legacy content retrieved successfully', 'LegacyContentService', { 
        id, 
        title: content.title 
      });

      return { success: true, data: content };

    } catch (error) {
      this.logger.error('Error in LegacyContentService.getById', 'LegacyContentService', { 
        error: (error as Error).message,
        id 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching the legacy content' 
      };
    }
  }

  /**
   * Get published legacy content with pagination
   */
  async getPublished(params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<LegacyContent>>> {
    try {
      this.logger.debug('LegacyContentService.getPublished called', 'LegacyContentService');

      const apiContent = await publicAPI.getLegacyContent();
      const publishedContent = apiContent
        .filter(item => item.is_published)
        .map(item => this.mapApiContent(item))
        .sort((a, b) => {
          const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
          const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
          return dateB - dateA; // Most recent first
        });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = publishedContent.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedContent = publishedContent.slice(startIndex, endIndex);

      const result: PaginatedResult<LegacyContent> = {
        items: paginatedContent,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Published legacy content retrieved', 'LegacyContentService', {
        total,
        page,
        itemCount: paginatedContent.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in LegacyContentService.getPublished', 'LegacyContentService', { 
        error: (error as Error).message 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching published legacy content' 
      };
    }
  }

  /**
   * Get legacy content by category with pagination
   */
  async getByCategory(category: string, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<LegacyContent>>> {
    try {
      this.logger.debug('LegacyContentService.getByCategory called', 'LegacyContentService', { category, params });

      if (!category || typeof category !== 'string') {
        return { success: false, error: 'Category parameter is required' };
      }

      const apiContent = await publicAPI.getLegacyContent();
      const filteredContent = apiContent
        .filter(item => 
          item.category && 
          item.category.toLowerCase().includes(category.toLowerCase())
        )
        .map(item => this.mapApiContent(item));

      // Sort by date (most recent first)
      const sortedContent = filteredContent.sort((a, b) => {
        const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
        const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
        return dateB - dateA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedContent.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedContent = sortedContent.slice(startIndex, endIndex);

      const result: PaginatedResult<LegacyContent> = {
        items: paginatedContent,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Legacy content by category retrieved', 'LegacyContentService', {
        category,
        total,
        page,
        itemCount: paginatedContent.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in LegacyContentService.getByCategory', 'LegacyContentService', { 
        error: (error as Error).message,
        category,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching legacy content by category' 
      };
    }
  }

  /**
   * Search legacy content by title or content
   */
  async search(query: string, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<LegacyContent>>> {
    try {
      this.logger.debug('LegacyContentService.search called', 'LegacyContentService', { query, params });

      if (!query || typeof query !== 'string' || query.trim().length === 0) {
        return { success: false, error: 'Search query is required' };
      }

      const searchTerm = query.toLowerCase().trim();

      const apiContent = await publicAPI.getLegacyContent();
      const filteredContent = apiContent
        .filter(item => 
          item.title.toLowerCase().includes(searchTerm) ||
          item.content.toLowerCase().includes(searchTerm)
        )
        .map(item => this.mapApiContent(item));

      // Sort by relevance (title matches first, then by date)
      const sortedContent = filteredContent.sort((a, b) => {
        const aTitleMatch = a.title.toLowerCase().includes(searchTerm);
        const bTitleMatch = b.title.toLowerCase().includes(searchTerm);
        
        if (aTitleMatch && !bTitleMatch) {return -1;}
        if (!aTitleMatch && bTitleMatch) {return 1;}
        
        // If both or neither match title, sort by date
        const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
        const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
        return dateB - dateA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedContent.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedContent = sortedContent.slice(startIndex, endIndex);

      const result: PaginatedResult<LegacyContent> = {
        items: paginatedContent,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Legacy content search completed', 'LegacyContentService', {
        query,
        total,
        page,
        itemCount: paginatedContent.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in LegacyContentService.search', 'LegacyContentService', { 
        error: (error as Error).message,
        query,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while searching legacy content' 
      };
    }
  }

  /**
   * Get legacy content by type/category (alias for getByCategory)
   */
  async getByType(type: string, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<LegacyContent>>> {
    return this.getByCategory(type, params);
  }

  /**
   * Get unique categories
   */
  async getCategories(): Promise<ServiceResult<string[]>> {
    try {
      this.logger.debug('LegacyContentService.getCategories called', 'LegacyContentService');

      const apiContent = await publicAPI.getLegacyContent();
      const categories = apiContent
        .map(item => item.category)
        .filter((category): category is string => category !== null && category !== undefined)
        .filter((category, index, array) => array.indexOf(category) === index) // Remove duplicates
        .sort((a, b) => a.localeCompare(b));

      this.logger.debug('Categories retrieved', 'LegacyContentService', {
        count: categories.length,
        categories
      });

      return { success: true, data: categories };

    } catch (error) {
      this.logger.error('Error in LegacyContentService.getCategories', 'LegacyContentService', { 
        error: (error as Error).message 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching categories' 
      };
    }
  }

  /**
   * Check if legacy content exists
   */
  async exists(id: string): Promise<ServiceResult<boolean>> {
    try {
      this.logger.debug('LegacyContentService.exists called', 'LegacyContentService', { id });

      if (!id || typeof id !== 'string') {
        return { success: false, error: 'Invalid content ID provided' };
      }

      const apiContent = await publicAPI.getLegacyContent();
      const exists = apiContent.some(item => item.id === id);

      this.logger.debug('Legacy content existence checked', 'LegacyContentService', { id, exists });

      return { success: true, data: exists };

    } catch (error) {
      this.logger.error('Error in LegacyContentService.exists', 'LegacyContentService', { 
        error: (error as Error).message,
        id 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while checking legacy content existence' 
      };
    }
  }

  /**
   * Create new legacy content (placeholder - requires admin API)
   */
  async create(_content: Partial<LegacyContent>): Promise<ServiceResult<LegacyContent>> {
    this.logger.warn('LegacyContentService.create called but not implemented', 'LegacyContentService');
    return { 
      success: false, 
      error: 'Legacy content creation is not available in the current implementation' 
    };
  }

  /**
   * Update legacy content (placeholder - requires admin API)
   */
  async update(_id: string, _updates: Partial<LegacyContent>): Promise<ServiceResult<LegacyContent>> {
    this.logger.warn('LegacyContentService.update called but not implemented', 'LegacyContentService');
    return { 
      success: false, 
      error: 'Legacy content updates are not available in the current implementation' 
    };
  }

  /**
   * Delete legacy content (placeholder - requires admin API)
   */
  async delete(_id: string): Promise<ServiceResult<boolean>> {
    this.logger.warn('LegacyContentService.delete called but not implemented', 'LegacyContentService');
    return { 
      success: false, 
      error: 'Legacy content deletion is not available in the current implementation' 
    };
  }

  /**
   * Apply sorting to legacy content array
   */
  private applySorting(content: LegacyContent[], sortBy: string, sortOrder: string): LegacyContent[] {
    return content.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'date_created': {
          const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
          const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
        case 'category': {
          const categoryA = a.category || '';
          const categoryB = b.category || '';
          comparison = categoryA.localeCompare(categoryB);
          break;
        }
        default: {
          const defaultDateA = a.date_created ? new Date(a.date_created).getTime() : 0;
          const defaultDateB = b.date_created ? new Date(b.date_created).getTime() : 0;
          comparison = defaultDateA - defaultDateB;
        }
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }
} 